import React from 'react';
import { useI18n } from '../context/I18nContext';
import { useCookies } from '../context/CookieContext';
import { X, Cookie, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';

const CookieBanner = () => {
  const { t } = useI18n();
  const { 
    showBanner, 
    showSettings, 
    preferences,
    acceptAll, 
    rejectNonEssential, 
    openSettings, 
    closeSettings,
    savePreferences 
  } = useCookies();

  const [tempPrefs, setTempPrefs] = React.useState({
    analytics: preferences?.analytics || false,
    advertising: preferences?.advertising || false
  });

  // Show floating cookie icon when consent is given but banner/settings are closed
  const showFloatingIcon = preferences && !showBanner && !showSettings;

  // Floating Cookie Settings Icon - positioned on left side, same style as accessibility widget
  if (showFloatingIcon) {
    return (
      <button
        onClick={openSettings}
        className="fixed bottom-4 left-4 z-40 bg-gray-700 hover:bg-gray-800 text-white p-4 rounded-full shadow-lg transition-all hover:scale-105"
        aria-label={t('cookie.settings.title') || 'Cookie Settings'}
        title={t('cookie.settings.title') || 'Cookie Settings'}
      >
        <Cookie className="h-6 w-6" />
      </button>
    );
  }

  if (!showBanner && !showSettings) return null;

  // Settings Modal
  if (showSettings) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cookie-settings-modal">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {t('cookie.settings.title')}
            </h3>
            <button onClick={closeSettings} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Necessary - always on */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="font-medium">{t('cookie.settings.necessary')}</Label>
                <p className="text-sm text-gray-500">{t('cookie.settings.necessaryDesc')}</p>
              </div>
              <Switch checked={true} disabled className="opacity-50" />
            </div>

            {/* Analytics */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="font-medium">{t('cookie.settings.analytics')}</Label>
                <p className="text-sm text-gray-500">{t('cookie.settings.analyticsDesc')}</p>
              </div>
              <Switch 
                checked={tempPrefs.analytics} 
                onCheckedChange={(checked) => setTempPrefs(p => ({ ...p, analytics: checked }))}
                data-testid="cookie-analytics-toggle"
              />
            </div>

            {/* Advertising */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="font-medium">{t('cookie.settings.advertising')}</Label>
                <p className="text-sm text-gray-500">{t('cookie.settings.advertisingDesc')}</p>
              </div>
              <Switch 
                checked={tempPrefs.advertising} 
                onCheckedChange={(checked) => setTempPrefs(p => ({ ...p, advertising: checked }))}
                data-testid="cookie-advertising-toggle"
              />
            </div>
          </div>

          <Button 
            onClick={() => savePreferences(tempPrefs)} 
            className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700"
            data-testid="cookie-save-btn"
          >
            {t('cookie.settings.save')}
          </Button>
        </div>
      </div>
    );
  }

  // Banner
  return (
    <div 
      className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50 p-4 md:p-6"
      data-testid="cookie-banner"
      role="dialog"
      aria-label={t('cookie.banner.title')}
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-1">
              <Cookie className="h-5 w-5 text-emerald-600" />
              {t('cookie.banner.title')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('cookie.banner.description')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={openSettings}
              data-testid="cookie-customize-btn"
            >
              {t('cookie.banner.customize')}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={rejectNonEssential}
              data-testid="cookie-reject-btn"
            >
              {t('cookie.banner.rejectNonEssential')}
            </Button>
            <Button 
              size="sm" 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={acceptAll}
              data-testid="cookie-accept-btn"
            >
              {t('cookie.banner.acceptAll')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
